import AnimationFadeIn from "@components/AnimationFadeIn";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import ButtonBackPage from "@components/ButtonBackPage";
import HeroCst from "@components/HeroCst";
import Table from "@components/Table";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import DownloadIcon from "@mui/icons-material/Download";
import { Box, Container, useTheme } from "@mui/material";
import {
  downloadBlobFile,
  downloadCertificate,
  onErrorMessage,
  ServicesURL,
} from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { CollectCampaignDto } from "../types";
import { ActionsType } from "src/interfaces/Common";
import { Visibility } from "@mui/icons-material";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import CustomTopToolbar from "@components/Table/components/CustomTopToolbar";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { buildDateStr } from "@utils/utilsDate";
import { MethodType } from "src/services/type";
import FormLoadingStateCst from "@components/FormLoadingStateCst";

const Monitoring: React.FC<{ page: PagesID }> = ({ page }) => {
  const { t, i18n } = useTranslation(["collect-campaign-page"]);
  const { t: inputTranslations } = useTranslation(["input"]);
  const theme = useTheme();
  const { id, idCampaign } = useParams();
  const { filters } = useFilters();
  const { user } = useAuth();
  const navigate = useNavigate();
  const filterId = `${user?.id_user}_${page}`;
  const [checkBoxData, setCheckBoxData] = useState<any>([]);
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();
  const handleCheckBox = (row: any) => {
    let dataBox = checkBoxData;
    let setData = setCheckBoxData;
    if (dataBox && dataBox.some((el: any) => el.id === row.id)) {
      const removeData = dataBox.filter((el: any) => el.id !== row.id);
      setData(removeData);
    } else {
      setData([...dataBox, row]);
    }
  };

  const { dataDetail: campaign } = useFetchDataDetail(
    ServicesURL.getCampaign
      .replace(":corporateId", id ? id : "1")
      .replace(":campaignId", idCampaign ?? ""),
    PagesID["corporate.collect-campaign.monitor"],
  );

  const { columns, dataTable, actionForRow, actionsTopToolbar, isLoading } =
    useFetchTable(
      ServicesURL.getCampaignUsers
        .replace(":corporateId", id ? id : "1")
        .replace(":campaignId", idCampaign ?? ""),
      PagesID["corporate.collect-campaign.monitor"],
      filters[filterId],
    );
  const customRenderTopToolbar = ({
    table,
  }: {
    table: any;
  }): React.ReactNode => {
    return columns ? (
      <CustomTopToolbar
        id={page}
        key={`custom_top_toolbar_${page}`}
        columns={columns
          .filter((column) => (column as any)["hasFilter"])
          .map((column) => ({
            ...column,
            visibility: true,
            visibilityInTable: true,
          }))}
        configurationTopToolbar={actionsToolbar}
        table={table}
        hiddenFilters={false}
        resetFormFilters={undefined}
        setHideHeader={undefined}
        counterElements={false}
        onToolbarChange={() => {}}
        modalTitle={undefined}
        noSummarySelected={undefined}
        filtersException={undefined}
        enableFullScreenToggle={false}
      />
    ) : (
      <></>
    );
  };

  const filteredColumns = columns
    ?.filter((col) => {
      return (
        !["score_formatted", "credits_formatted", "hours_formatted"].includes(
          col.accessorKey,
        ) ||
        ((campaign as CollectCampaignDto).unit === "HOURS" &&
          col.accessorKey === "hours_formatted") ||
        ((campaign as CollectCampaignDto).unit === "POINTS" &&
          col.accessorKey === "score_formatted") ||
        ((campaign as CollectCampaignDto).unit === "CREDITS" &&
          col.accessorKey === "credits_formatted")
      );
    })
    .filter((col) => col.accessorKey !== "completed");

  const actionsRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.key) {
        case "monitor": {
          // monitoring notification
          return {
            ...action,
            callBack: (el: any) => navigate(`campaign-user/${el.user_id}`),
            getIcon: () => <Visibility />,
          };
        }
        case "monitor-ext": {
          return {
            ...action,
            callBack: (el: any) =>
              navigate(`campaign-user/${el.user_id}/external-training`),
            getIcon: () => <Visibility />,
          };
        }
        case "download": {
          return {
            ...action,
            callBack: async (el: any) => {
              await downloadCertificate(
                ServicesURL.downloadCampaignCertificate
                  .replace(":corporateId", id ? id : "1")
                  .replace(":campaignId", idCampaign ? idCampaign : "")
                  .replace(":userId", el.user_id),
                `certificate_${idCampaign}_${el.user_id}.pdf`,
                { Accept: "application/pdf" },
              );
            },
            getIcon: () => (
              <DownloadIcon
                color={campaign.has_template ? "primary" : undefined}
                sx={
                  campaign.has_template
                    ? {
                        "&.MuiSvgIcon-root": {
                          fill: `${theme.palette.primary.main}!important`,
                        },
                      }
                    : undefined
                }
              />
            ),
          };
        }
        default:
          return action;
      }
    });
  }, [actionForRow]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = actionsRow && [...actionsRow];
    configRowNew = configRowNew.map((el: ActionsType) => {
      if (el.type === "delete") {
        // return { ...el, disabled: !row.original.deletable };
        return { ...el };
      }

      if (el.type === "edit") {
        // return { ...el, disabled: !row.original.editable };
        return { ...el };
      }

      if (el.type === "button" && el.key === "download") {
        return { ...el, disabled: !campaign.has_template };
      }
      return { ...el };
    });

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
        checkBoxData={[checkBoxData]}
        setCheckBoxData={handleCheckBox}
      />,
    ];
  };

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return actionsTopToolbar?.map((action: ActionsType) => {
      switch (action.type) {
        case "button": {
          if (action.type === "button" && action.icon === "download") {
            const hasDownloadCertificatePermission = user?.permissions.find(
              (userPermission) => userPermission === action.permission,
            );
            if (!hasDownloadCertificatePermission) {
              return {} as ActionsType;
            }

            return {
              ...action,
              disabled: !campaign.has_template,
              labelTranslate: (action.label as any)[i18n.language],
              callBack:
                checkBoxData.length && campaign.has_template
                  ? async (el: any) => {
                      try {
                        setOpenToast({
                          s: StatusEnum.LOADING,
                          message: "loading",
                        });

                        const fileName = `campaign_certificates_${buildDateStr(new Date())}.zip`;

                        const query = new URLSearchParams();
                        const issuedCertificates = (
                          checkBoxData as any[]
                        ).forEach((row) => {
                          query.append("user_id", row.id);
                        });

                        await downloadBlobFile(
                          `${ServicesURL.downloadCampaignCertificates.replace(":corporateId", id ?? "1").replace(":campaignId", idCampaign ?? "")}?${query.toString()}`,
                          {
                            issuedCertificates: issuedCertificates,
                          },
                          fileName,
                          MethodType.GET,
                        );
                        setOpenToast({ s: StatusEnum.OK, message: "" });
                      } catch (error) {
                        const message = onErrorMessage(error);
                        setOpenToast({ s: StatusEnum.KO, message: message });
                        console.error(error);
                      }
                    }
                  : null,
              getIcon: () => <DownloadIcon />,
            } as ActionsType;
          }
          return {
            ...action,
            labelTranslate: (action.label as any)[i18n.language],
          } as ActionsType;
        }
        default:
          return action;
      }
    });
  }, [actionsTopToolbar, id, navigate, i18n, dataTable, user?.permissions]);

  const renderCell = (cel: any, value: any, row: any) => {
    return value !== undefined && value !== null ? value.toString() : "";
  };

  const formatNumber = (value?: number): string =>
    Number.isInteger(value) ? (value ?? 0).toString() : (value ?? 0).toFixed(2);

  const tableData = useMemo(() => {
    const target = formatNumber((campaign as CollectCampaignDto).target);
    return dataTable?.output
      .filter(({ user_id }: { user_id?: number }) => !!user_id)
      .map((row: any) => {
        const newRow = { ...row };
        if ((campaign as CollectCampaignDto).unit === "HOURS") {
          newRow.hours_formatted = `${formatNumber(row.hours)}/${target}`;
        }
        if ((campaign as CollectCampaignDto).unit === "POINTS") {
          newRow.score_formatted = `${formatNumber(row.score)}/${target}`;
        }
        if ((campaign as CollectCampaignDto).unit === "CREDITS") {
          newRow.credits_formatted = `${formatNumber(row.credits)}/${target}`;
        }
        newRow.id = row.user_id;
        return newRow;
      });
  }, [dataTable, campaign]);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-survey.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("monitoring")} description={""} />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          {isLoading ? (
            <FormLoadingStateCst />
          ) : (
            <Table
              id={page}
              data={tableData || []}
              columns={filteredColumns || []}
              configurationTopToolbar={actionsToolbar}
              configurationActionForRow={actionsRow}
              renderRowActions={customRenderRowActions}
              totalPages={dataTable?.metadata?.total_elements}
              renderCell={renderCell}
              renderTopToolbar={customRenderTopToolbar}
            />
          )}
        </Box>
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={t("download-certificates.title", { ns: "modal" })}
        description={""}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        removeButtons={true}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};
export default Monitoring;
